import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { ProColumns, ProTable, ActionType } from "@ant-design/pro-components";
import { App, Popconfirm, Typography, Button, Select, Space } from "antd";

import dayjs from "../../../config/dayjs";
import { leaveStatusLabels, leaveTypeLabels } from "../../../constant/enum";
import { getUser } from "../../../services/auth";
import { removeEmptyParams } from "../../../utils/listQueryHelper";
import { waitTime } from "../../../utils/WaitTime";
import {
  archiveLeave,
  downloadLeaveFileFromS3,
  getLeaves,
  getUserLeaves,
  updateLeaveStatus,
} from "../api/leaveAPI";
import { ILeaveResponse } from "../type/leave";

const { Text } = Typography;

export const LeaveTable = () => {
  const navigate = useNavigate();
  const actionRef = useRef<ActionType>();
  const { message } = App.useApp();
  const user = getUser();
  const role = user.role;
  const [isActionRequired, setIsActionRequired] = useState(false);

  const ActionCell: React.FC<{ record: ILeaveResponse }> = ({ record }) => {
    const [selectedAction, setSelectedAction] = useState(record.status);

    const statusOptions = [
      { label: "Pending", value: "pending", disabled: true },
      { label: "Approve", value: "approved" },
      { label: "Reject", value: "rejected" },
    ];

    return (
      <Space direction="horizontal">
        <Select
          defaultValue={selectedAction}
          onChange={setSelectedAction}
          options={statusOptions}
        />
        <Popconfirm
          title={`Are you sure you want to ${selectedAction} this leave?`}
          description="This action cannot be undone."
          onConfirm={async () => {
            try {
              await updateLeaveStatus(record.id, { status: selectedAction });
              message.success("Leave status updated successfully");
              actionRef.current?.reload();
            } catch (error: any) {
              message.error(error.message || "Failed to update leave status");
            }
          }}
          disabled={role !== "admin" && role !== "branchAdmin"}
        >
          <Button
            type="primary"
            style={{ borderRadius: 4 }}
            disabled={
              (role !== "admin" && role !== "branchAdmin") ||
              record.status === "approved" ||
              record.status === "rejected" ||
              selectedAction === record.status
            }
          >
            Confirm
          </Button>
        </Popconfirm>
      </Space>
    );
  };

  const columns: ProColumns<ILeaveResponse>[] = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      search: false,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      search: role === "admin" || role === "branchAdmin" ? true : false,
      render: (_, record) => <span>{record.user.name}</span>,
    },
    {
      title: "Leave Type",
      dataIndex: "leaveType",
      key: "leaveType",
      valueType: "select",
      valueEnum: leaveTypeLabels,
      render: (_, record) => {
        const leaveType = record.leaveType;
        const leaveTypeLabel =
          leaveTypeLabels[leaveType as keyof typeof leaveTypeLabels] ??
          leaveType;

        return <span>{leaveTypeLabel}</span>;
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search leave type",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
      search: {
        transform: (value) => ({ leaveType: value }),
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      valueType: "date",
      search: false,
      sorter: true,
      render: (_, record) => {
        const date = dayjs(record.startDate).format("DD/MM/YYYY");

        return date;
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      valueType: "date",
      search: false,
      sorter: true,
      render: (_, record) => {
        const date = dayjs(record.endDate).format("DD/MM/YYYY");

        return date;
      },
    },
    {
      title: "Total Days",
      dataIndex: "totalDays",
      key: "totalDays",
      valueType: "digit",
      search: false,
      width: 150,
      align: "center",
      render: (_, record) => {
        if (
          (record.leaveType === "annual" ||
            record.leaveType === "medical" ||
            record.leaveType === "hospitalization" ||
            record.leaveType === "maternity") &&
          (role === "admin" || role === "branchAdmin")
        ) {
          const leaveType = record.leaveType as
            | "annual"
            | "medical"
            | "hospitalization"
            | "maternity";
          const balance = record.user.leaveBalance?.[leaveType]?.balance ?? 0;
          const totalDays = record.totalDays ?? 0;
          return `${totalDays} (Balance: ${balance})`;
        }
        return record.totalDays ?? "-";
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      valueType: "text",
      search: false,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      valueType: "select",
      valueEnum: leaveStatusLabels,
      width: 100,
      align: "center",
      sorter: true,
      render: (_, record) => {
        const status = record.status;
        const statusLabel =
          leaveStatusLabels[status as keyof typeof leaveStatusLabels] ?? status;

        return (
          <Text
            type={
              status === "pending"
                ? "warning"
                : status === "approved"
                ? "success"
                : "danger"
            }
          >
            {statusLabel}
          </Text>
        );
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search status",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
      search: {
        transform: (value) => ({ status: value }),
      },
    },
    {
      title: "Attachment",
      dataIndex: "fileUrl",
      key: "fileUrl",
      valueType: "text",
      search: false,
      align: "center",
      render: (_, record) => {
        const handleFileDownload = async (fileUrl: string) => {
          try {
            await downloadLeaveFileFromS3(record.id, fileUrl);
          } catch (error) {
            message.error("Failed to open the file");
          }
        };
        if (record.fileUrl) {
          return (
            <Button
              type="link"
              onClick={() => handleFileDownload(record.fileUrl!)}
            >
              Open File
            </Button>
          );
        } else {
          return <span>-</span>;
        }
      },
    },
    ...(isActionRequired && (role === "admin" || role === "branchAdmin")
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            search: false,
            align: "center" as const,
            render: (_: any, record: ILeaveResponse) => {
              if (record.status === "pending") {
                return <ActionCell record={record} />;
              } else {
                return null;
              }
            },
          },
        ]
      : []),
    ...(role === "staff" || role === "lawyer"
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            search: false,
            align: "center" as const,
            render: (_: any, record: ILeaveResponse) => (
              <Space>
                <EditOutlined
                  style={{
                    color: record.status === "pending" ? "blue" : "grey",
                    cursor:
                      record.status === "pending" ? "pointer" : "not-allowed",
                  }}
                  onClick={() =>
                    record.status === "pending" &&
                    navigate(`/leaves/${record.id}/edit`, {
                      state: {
                        leaveId: record.id,
                        displayName: record.leaveType,
                      },
                    })
                  }
                />
                <Popconfirm
                  title="Are you sure you want to delete this leave?"
                  description="This action cannot be undone."
                  onConfirm={async () => {
                    try {
                      const response = await archiveLeave(record.id);
                      message.success(response.message);
                      actionRef.current?.reload();
                    } catch (error: any) {
                      message.error(error.message);
                    }
                  }}
                  disabled={record.status !== "pending"}
                >
                  <DeleteOutlined
                    style={{
                      color: record.status === "pending" ? "red" : "grey",
                      cursor:
                        record.status === "pending" ? "pointer" : "not-allowed",
                    }}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <ProTable<ILeaveResponse>
        cardBordered
        form={{ layout: "vertical" }}
        scroll={{ x: "max-content" }}
        request={async (params, sort, filter) => {
          try {
            params = removeEmptyParams(params);
            if (role === "admin" || role === "branchAdmin") {
              const data = await getLeaves({ filter, sort, params });
              const leaves = data.results;
              // Check if there are any pending leaves and update state accordingly
              const hasPending = leaves.some(
                (leave) => leave.status === "pending"
              );
              setIsActionRequired(hasPending);

              return {
                data: leaves,
                success: true,
                total: data.totalResults,
              };
            } else {
              const data = await getUserLeaves(user.id, {
                filter,
                sort,
                params,
              });
              const leaves = data.results;
              // Check if there are any pending leaves and update state accordingly
              const hasPending = leaves.some(
                (leave) => leave.status === "pending"
              );
              setIsActionRequired(hasPending);

              return {
                data: leaves,
                success: true,
                total: data.totalResults,
              };
            }
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 10,
            };
          }
        }}
        rowKey={(data) => data.id}
        columns={columns}
        editable={{
          type: "multiple",
          onSave: async (_rowKey, _data, _row) => {
            await waitTime(2000);
          },
        }}
        actionRef={actionRef}
      />
    </>
  );
};
